import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import backend_url from '../config/config';

const AuthContext = createContext(null);

// Helper function to get redirect path based on user role (currently unused)
// const getRedirectPathForRole = (role) => {
//   switch (role) {
//     case 'custom_role_sk':
//       return '/branch-order/create-order';
//     case 'custom_role_ck':
//       return '/central-kitchen/dashboard';
//     case 'master_admin':
//     case 'admin':
//       return '/dashboard';
//     default:
//       return '/dashboard';
//   }
// };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async (token) => {
    try {
      const response = await axios.get(`${backend_url}/users/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching user profile:', error);
      }
      if (error.response?.data?.code === 'TOKEN_EXPIRED' || error.response?.status === 401) {
        // Token expired or invalid, redirect to login
        localStorage.removeItem('token');
        window.location.href = '/';
      }
      return false;
      
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in on mount (for page refresh)
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserProfile(token);
    } else {
      setLoading(false);
    }
  }, [fetchUserProfile]);

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call logout endpoint to invalidate token on server
        await axios.post(`${backend_url}/users/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
    } finally {
      // Clear local storage and redirect to login
      localStorage.removeItem('token');
      setUser(null);
      window.location.href = '/';
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 AuthContext: Starting login process...');
        console.log('🔐 AuthContext: Backend URL:', backend_url);
        console.log('🔐 AuthContext: Login data:', { email, password: '***' });
      }
      
      const response = await axios.post(`${backend_url}/users/login`, {
        email,
        password
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 AuthContext: Login response received:', {
          status: response.status,
          hasToken: !!response.data.token,
          userData: {
            name: response.data.name,
            email: response.data.email,
            role: response.data.role
          }
        });
      }
      
      const { token, ...userData } = response.data;
      localStorage.setItem('token', token);
      setUser(userData);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 AuthContext: User state updated, token stored');
      }
      
      // Set up automatic logout after 8 hours
      setTimeout(() => {
        logout();
      }, 8 * 60 * 60 * 1000); // 8 hours in milliseconds
      
      // Let the RoleBasedRedirect component handle the redirect
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 AuthContext: Login successful, user state updated');
      }
      
      return { success: true, user: userData };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('🔐 AuthContext: Login error:', error);
        console.error('🔐 AuthContext: Error details:', {
          status: error.response?.status,
          message: error.response?.data?.message,
          code: error.code,
          config: {
            url: error.config?.url,
            method: error.config?.method
          }
        });
      }
      
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    } finally {
      setLoading(false);
    }
  }, [logout]);

  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${backend_url}/users/register`, userData);
      const { token, ...user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      
      // Set up automatic logout after 8 hours
      setTimeout(() => {
        logout();
      }, 8 * 60 * 60 * 1000); // 8 hours in milliseconds
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      };
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Permission checking functions - memoized
  const hasPermission = useCallback((action) => {
    if (!user) return false;
    if (user.isMasterAdmin) return true;
    return user.permissions?.[action] || false;
  }, [user]);

  const canCreate = useCallback(() => hasPermission('canCreate'), [hasPermission]);
  const canEdit = useCallback(() => hasPermission('canEdit'), [hasPermission]);
  const canDelete = useCallback(() => hasPermission('canDelete'), [hasPermission]);
  const canView = useCallback(() => hasPermission('canView'), [hasPermission]);
  const isMasterAdmin = useCallback(() => user?.isMasterAdmin || false, [user]);

  // New page access functions - memoized
  const canAccessPage = useCallback((pagePath) => {
    if (!user) return false;
    if (user.isMasterAdmin) return true;
    
    // Check if user has access to all pages
    if (user.allowedPages && user.allowedPages.includes('*')) {
      return true;
    }
    
    // Check specific page access
    if (user.allowedPages && user.allowedPages.length > 0) {
      return user.allowedPages.includes(pagePath);
    }
    
    // Fallback to basic permissions
    return user.permissions?.canView || false;
  }, [user]);

  const getAllowedPages = useCallback(() => {
    if (!user) return [];
    if (user.isMasterAdmin) return ['*']; // All pages
    return user.allowedPages || [];
  }, [user]);

  const getRoleDescription = useCallback(() => {
    if (!user) return '';
    return user.roleDescription || 'No description available';
  }, [user]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    // Permission functions
    hasPermission,
    canCreate,
    canEdit,
    canDelete,
    canView,
    isMasterAdmin,
    // New page access functions
    canAccessPage,
    getAllowedPages,
    getRoleDescription
  }), [
    user,
    loading,
    login,
    register,
    logout,
    hasPermission,
    canCreate,
    canEdit,
    canDelete,
    canView,
    isMasterAdmin,
    canAccessPage,
    getAllowedPages,
    getRoleDescription
  ]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 